import { useMemo, useState, type ReactNode } from "react";

import { Trash2 } from "lucide-react";

import { Badge } from "../../../../ui/badge";
import { Button } from "../../../../ui/button";
import { Card, CardContent } from "../../../../ui/card";
import { Checkbox } from "../../../../ui/checkbox";
import { Input } from "../../../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../../../ui/table";
import { usd } from "../../../../components/portfolio-risk-concentration/format";
import { ScenarioRetireDialog } from "../../../../components/portfolio-risk-concentration/ScenarioRetireDialog";
import { ScenarioSpinner } from "../../../../components/portfolio-risk-concentration/ScenarioSpinner";
import { hasTier } from "../../../../components/portfolio-risk-concentration/access-control";
import { cn } from "../../../../utils/common/cn";
import {
  useScenarioReconciliation,
  type ScenarioReconciliationRow,
} from "../../../../utils/portfolio-risk-concentration/api/scenario";

const ALL_BATCHES = "ALL";
const PAGE_SIZE = 10;

/** A generic client-side-paginated table, shared by the Unreconciled and Reconciled tables
 *  below -- same page-size/Prev-Next convention as MatchedContractsTable in
 *  ScenarioApplyStep.tsx and every other paginated table in this product. */
function PaginatedTable({ rows, children }: { rows: unknown[]; children: (page: unknown[]) => ReactNode }) {
  const [offset, setOffset] = useState(0);
  const total = rows.length;
  const page = rows.slice(offset, offset + PAGE_SIZE);
  return (
    <>
      {children(page)}
      <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
        <span>
          {total === 0 ? "0 of 0" : `${offset + 1}–${Math.min(offset + PAGE_SIZE, total)} of ${total}`}
        </span>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={offset === 0}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Prev
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={offset + PAGE_SIZE >= total}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      </div>
    </>
  );
}

/**
 * Scenario Reconciliation tab: only APPROVED-or-later proposals reach here (PENDING/REJECTED
 * never do -- see scenario.py's module docstring). Every batch card and row is a live
 * recompute against the current bankdwn_daily snapshot each time this loads, joined against
 * prc_scenario_pledge_reconciliation for the one thing that IS a stable, stamped fact: when a
 * contract was first observed matching (reconciledAt), never a live "as of today" label.
 */
export default function ScenarioReconciliationPanel() {
  const [showRetired, setShowRetired] = useState(false);
  const { data, isLoading } = useScenarioReconciliation(undefined, showRetired);
  const [batchFilter, setBatchFilter] = useState(ALL_BATCHES);
  const [dateFilter, setDateFilter] = useState("");
  // The scenario whose retirement is being requested (the trash icon on a card). Retiring is of the whole scenario, so its other
  // destinations' cards are covered too.
  const [retiring, setRetiring] = useState<{ scenarioRef: string; label: string } | null>(null);
  const canRequestRetire = hasTier("reviewer");

  const batches = data?.batches ?? [];

  function matches(row: { proposalId: string; batchDate: string | null }): boolean {
    if (batchFilter !== ALL_BATCHES && row.proposalId !== batchFilter) return false;
    if (dateFilter && row.batchDate !== dateFilter) return false;
    return true;
  }

  const unreconciled = useMemo(
    () => (data?.unreconciled ?? []).filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, batchFilter, dateFilter],
  );
  const reconciled = useMemo(
    () => (data?.reconciled ?? []).filter(matches),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, batchFilter, dateFilter],
  );

  return (
    <div>
      <p className="text-xs text-muted-foreground">
        Only approved proposals reach reconciliation. Every contract, matched or not, nothing
        hidden once it's confirmed.
      </p>
      <label className="mt-2 flex w-fit items-center gap-1.5 text-xs text-muted-foreground">
        <Checkbox checked={showRetired} onCheckedChange={(v) => setShowRetired(!!v)} />
        Show retired scenarios
      </label>

      {isLoading ? (
        <ScenarioSpinner label="Loading reconciliation…" />
      ) : batches.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          No approved proposals yet. Reconciliation has nothing to check.
        </p>
      ) : (
        <>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {batches.map((b) => (
              <Card key={b.proposalId} className={cn(b.scenarioStatus === "RETIRED" && "opacity-60")}>
                <CardContent className="py-4">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-xs text-muted-foreground">{b.batchLabel}</p>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {b.scenarioStatus === "RETIRED" ? (
                        <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Retired
                        </Badge>
                      ) : b.retirePending ? (
                        <Badge variant="outline" className="border-amber/50 text-[10px] uppercase tracking-wider text-amber">
                          Retire pending
                        </Badge>
                      ) : canRequestRetire ? (
                        <button
                          type="button"
                          aria-label={`Retire scenario ${b.batchLabel}`}
                          title="Request to retire this scenario (an approver decides; nothing is deleted)"
                          className="text-muted-foreground transition-colors hover:text-destructive"
                          onClick={() => setRetiring({ scenarioRef: b.scenarioRef, label: b.batchLabel })}
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  <p
                    className={cn(
                      "mt-1 text-2xl font-semibold",
                      b.unreconciledCount === 0 ? "text-[color:var(--approve)]" : "text-destructive",
                    )}
                  >
                    {b.unreconciledCount}
                  </p>
                  <p className="text-[11px] text-muted-foreground">of {b.totalCount} unreconciled</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Select value={batchFilter} onValueChange={setBatchFilter}>
              <SelectTrigger className="h-9 w-56 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_BATCHES}>All batches</SelectItem>
                {batches.map((b) => (
                  <SelectItem key={b.proposalId} value={b.proposalId}>
                    {b.batchLabel}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              type="date"
              className="h-9 w-40 text-xs [&::-webkit-calendar-picker-indicator]:invert"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
          </div>

          <div className="mt-4">
            <p className="text-sm font-semibold">Unreconciled</p>
            <div className="mt-2 overflow-hidden rounded-lg border border-border">
              <PaginatedTable rows={unreconciled}>
                {(page) => (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contract #</TableHead>
                        <TableHead>Schedule #</TableHead>
                        <TableHead>Batch</TableHead>
                        <TableHead className="text-right">Financed Amount</TableHead>
                        <TableHead>Approved Code</TableHead>
                        <TableHead>Current Code</TableHead>
                        <TableHead className="text-right">Days Unreconciled</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {page.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                            Nothing unreconciled.
                          </TableCell>
                        </TableRow>
                      )}
                      {(page as ScenarioReconciliationRow[]).map((r) => (
                        <TableRow key={r.contractKey}>
                          <TableCell className="whitespace-nowrap text-xs">{r.contractNumber}</TableCell>
                          <TableCell className="text-xs">{r.scheduleNumber ?? "—"}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs">{r.batchLabel}</TableCell>
                          <TableCell className="text-right">{usd(r.financedAmount)}</TableCell>
                          <TableCell className="text-xs">{r.approvedCode}</TableCell>
                          <TableCell className="text-xs text-destructive">{r.currentCode ?? "—"}</TableCell>
                          <TableCell className="text-right text-xs">{r.daysUnreconciled ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </PaginatedTable>
            </div>
          </div>

          <div className="mt-6">
            <p className="text-sm font-semibold">Reconciled</p>
            <div className="mt-2 overflow-hidden rounded-lg border border-border">
              <PaginatedTable rows={reconciled}>
                {(page) => (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Contract #</TableHead>
                        <TableHead>Schedule #</TableHead>
                        <TableHead>Batch</TableHead>
                        <TableHead className="text-right">Financed Amount</TableHead>
                        <TableHead>Approved Code</TableHead>
                        <TableHead>Current Code</TableHead>
                        <TableHead>Reconciled</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {page.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                            Nothing reconciled yet.
                          </TableCell>
                        </TableRow>
                      )}
                      {(page as ScenarioReconciliationRow[]).map((r) => (
                        <TableRow key={r.contractKey}>
                          <TableCell className="whitespace-nowrap text-xs">{r.contractNumber}</TableCell>
                          <TableCell className="text-xs">{r.scheduleNumber ?? "—"}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs">{r.batchLabel}</TableCell>
                          <TableCell className="text-right">{usd(r.financedAmount)}</TableCell>
                          <TableCell className="text-xs">{r.approvedCode}</TableCell>
                          <TableCell className="text-xs text-[color:var(--approve)]">{r.currentCode ?? "—"}</TableCell>
                          <TableCell className="whitespace-nowrap text-xs">
                            {r.reconciledAt ? new Date(r.reconciledAt).toLocaleDateString() : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </PaginatedTable>
            </div>
          </div>
        </>
      )}
      {retiring && (
        <ScenarioRetireDialog
          open
          onOpenChange={(o) => {
            if (!o) setRetiring(null);
          }}
          scenarioRef={retiring.scenarioRef}
          label={retiring.label}
        />
      )}
    </div>
  );
}
